import type { ActorRefFrom, SnapshotFrom } from "xstate";

import type { PlayerId } from "@/types/branded";

import { computeRounds } from "./graphUtils";
import type {
  KnockoutContext,
  knockoutMachine,
  KnockoutMatchActorRef,
} from "./knockout/knockout.machine";
import type {
  RoundRobinContext,
  roundRobinMachine,
  RoundRobinMatchActorRef,
} from "./roundrobin/roundrobin.machine";
import type { SwissContext, swissMachine, SwissMatchActorRef } from "./swiss/swiss.machine";
import type { MatchId } from "./types";
import type { Graph } from "./types/graph";
import type { TournamentNode } from "./types/node";
import type { RoundRobinParticipantStats, SwissParticipantStats } from "./types/participantStat";

export type KnockoutTournamentActorRef = ActorRefFrom<typeof knockoutMachine>;
export type RoundRobinTournamentActorRef = ActorRefFrom<typeof roundRobinMachine>;
export type SwissTournamentActorRef = ActorRefFrom<typeof swissMachine>;

export type TournamentActorRef =
  | KnockoutTournamentActorRef
  | RoundRobinTournamentActorRef
  | SwissTournamentActorRef;

type TournamentContext = KnockoutContext | RoundRobinContext | SwissContext;
type TournamentMatchActorRef = KnockoutMatchActorRef | RoundRobinMatchActorRef | SwissMatchActorRef;

export type ActiveTournamentMatch = {
  id: MatchId;
  players: [PlayerId | null | undefined, PlayerId | null | undefined];
  round?: number;
  bracket?: number;
  isChampionship: boolean;
  canDraw: boolean;
};

export type TournamentStanding = RoundRobinParticipantStats | SwissParticipantStats;

const getTournamentContext = (ref: TournamentActorRef): TournamentContext =>
  ref.getSnapshot().context;

const getSortedNodes = (context: TournamentContext): TournamentNode[] => {
  const rounds = computeRounds(context.graph as Graph);
  const matchesByRound = new Map<number, MatchId[]>();
  for (const [matchId, round] of rounds) {
    if (!matchesByRound.has(round)) {
      matchesByRound.set(round, []);
    }
    matchesByRound.get(round)!.push(matchId);
  }

  const sortedNodes: TournamentNode[] = [];
  const sortedRounds = Array.from(matchesByRound.keys()).sort((a, b) => a - b);
  for (const round of sortedRounds) {
    const matchIds = matchesByRound.get(round)!;
    for (const matchId of matchIds) {
      const node = context.graph.nodes.get(matchId);
      if (node) {
        sortedNodes.push(node);
      }
    }
  }

  return sortedNodes;
};

const canDrawInTournament = (context: TournamentContext): boolean => "standings" in context;

export const isTournamentDone = (ref: TournamentActorRef): boolean =>
  ref.getSnapshot().status === "done";

export const subscribeToTournament = (
  ref: TournamentActorRef,
  listener: (snapshot: SnapshotFrom<TournamentActorRef>) => void,
): { unsubscribe: () => void } =>
  (
    ref.subscribe as (listener: (snapshot: SnapshotFrom<TournamentActorRef>) => void) => {
      unsubscribe: () => void;
    }
  )(listener);

export const getTournamentResults = (ref: TournamentActorRef): PlayerId[][] =>
  isTournamentDone(ref) ? (ref.getSnapshot().output ?? []) : [];

export const getTournamentStandings = (ref: TournamentActorRef): TournamentStanding[] => {
  const context = getTournamentContext(ref);
  if (!("standings" in context)) return [];

  return Array.from((context.standings as Map<PlayerId, TournamentStanding>).values()).sort(
    (a, b) => {
      const aTieBreaker = "buchholz" in a ? a.buchholz : a.tiebreakers;
      const bTieBreaker = "buchholz" in b ? b.buchholz : b.tiebreakers;
      return b.points - a.points || bTieBreaker - aTieBreaker;
    },
  );
};

export const getActiveTournamentMatch = (
  ref: TournamentActorRef,
): ActiveTournamentMatch | undefined => {
  const context = getTournamentContext(ref);
  const { matchRefs } = context;

  for (const node of getSortedNodes(context)) {
    const matchRef = matchRefs.get(node.id);
    const matchSnapshot = matchRef?.getSnapshot();
    if (!matchSnapshot?.matches("ready")) continue;

    return {
      id: node.id,
      players: matchSnapshot.context.players,
      round: "round" in node ? node.round : undefined,
      bracket: "bracket" in node ? node.bracket : undefined,
      isChampionship: "isChampionship" in node ? node.isChampionship : false,
      canDraw: canDrawInTournament(context),
    };
  }

  return undefined;
};

export const sendTournamentResult = (
  ref: TournamentActorRef,
  matchId: string,
  winnerId: PlayerId | "draw",
): void => {
  const context = getTournamentContext(ref);
  if (winnerId === "draw" && !canDrawInTournament(context)) return;

  const matchRef = context.matchRefs.get(matchId as MatchId);
  if (!matchRef) return;

  const matchSnapshot = matchRef?.getSnapshot();
  if (!matchSnapshot?.matches("ready")) return;

  matchRef.send({ type: "REPORT_RESULT", winnerId: winnerId as never });
};
